import { useNavigate } from "react-router-dom";
import { Fragment, SyntheticEvent, useEffect, useState } from "react";

import moment from "moment";
import {
  Badge,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Approval from "@mui/icons-material/ApprovalOutlined";
import { visuallyHidden } from "@mui/utils";

import conf from "../../conf";
import Refresh from "../utils/Refresh";
import Spinner from "../utils/Spinner";
import { useAuth } from "../auth/AuthProvider";
import EmptyTableRow from "../utils/Empty";
import SectionHeader from "../utils/SectionHeader";
import usePagination from "../../hooks/pagination";
import ProtectedRoute from "../auth/ProtectedRoute";
import LinearIndeterminate from "../utils/LinearLoader";
import { a11yProps, CustomTabPanel } from "./Rows";
import {
  RequestSchema,
  useProxyRequests,
  AxiosMutationRequest,
  useProxyApprove,
  useProxyRequestDetail,
  useProxyReject,
} from "../../hooks/proxy/requests";

export const PendingRow = (props: {
  request: RequestSchema;
  approve: AxiosMutationRequest;
  reject: AxiosMutationRequest;
}) => {
  const {
    request: { id, requester, subject, project, updatedAt, status },
    approve,
    reject,
  } = props;

  const { user } = useAuth();
  const showEdit = ["org.project.manager"].includes(
    user.activeOrganization.role
  );
  const disableActions = requester === user.email;

  const navigate = useNavigate();
  const handleClick = () => navigate(`/requests/${id}`);

  const [comment, setComment] = useState("");
  const [openDetail, setOpenDetail] = useState(false);

  const detail = useProxyRequestDetail({ id });

  const toggleDetail = () => setOpenDetail(!openDetail);
  const handleReject = () => reject.mutate({ id, comment });
  const handleApprove = () => approve.mutate({ id, comment });

  const chip = <Chip color="warning" label={status} onClick={handleClick} />;

  return (
    <Fragment>
      <TableRow
        hover
        key={id}
        onClick={toggleDetail}
        sx={conf.STYLE.LAST_CHILD_NO_BORDER}
        style={{ cursor: "pointer" }}
      >
        <TableCell>{moment(updatedAt).fromNow()}</TableCell>

        <TableCell align="right">{chip}</TableCell>

        <TableCell align="right">{requester}</TableCell>

        <TableCell align="right">{subject || "--"}</TableCell>

        <TableCell align="right">
          <b>{project.name ?? "--"}</b>
        </TableCell>
      </TableRow>

      {openDetail && (
        <TableRow key={`${id}-detail`} sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
          <TableCell colSpan={7}>
            <Box>
              <TextField
                multiline
                fullWidth
                aria-readonly
                margin="normal"
                variant="filled"
                InputProps={{ readOnly: true }}
                defaultValue={detail?.data?.data.message}
              />

              {showEdit && (
                <Stack
                  spacing={2}
                  marginTop={1}
                  direction="row"
                  alignItems="center"
                  justifyContent="end"
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Comment (optional)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={disableActions}
                  />

                  <Button
                    fullWidth
                    color="success"
                    variant="contained"
                    onClick={handleApprove}
                    disabled={disableActions}
                  >
                    {approve.isPending ? <Spinner sx={{ mx: 4 }} /> : "Approve"}
                  </Button>

                  <Button
                    fullWidth
                    color="error"
                    variant="contained"
                    onClick={handleReject}
                    disabled={disableActions}
                  >
                    {reject.isPending ? <Spinner sx={{ mx: 4 }} /> : "Reject"}
                  </Button>
                </Stack>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

const RequestsPending = ({ isLite }: { isLite?: boolean }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);

  const paginate = usePagination({ defaultSort: "updatedAt" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const reject = useProxyReject();
  const approve = useProxyApprove();
  const pending = useProxyRequests({ status: "pending", ...body });

  const handleChange = (e: SyntheticEvent, value: number) => setValue(value);

  useEffect(() => {
    document.title = "[semiauth] | requests";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl">
        <SectionHeader
          title="Requests"
          icon={
            <Badge color="warning" badgeContent={pending.data?.meta.total}>
              <Approval />
            </Badge>
          }
          buttons={[
            <TablePagination
              key={"requests-pending-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={pending?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh
              key={"requests-pending-refresh"}
              isFetching={pending.isFetching}
              onClick={pending.refetch}
            />,
          ]}
        />

        <Box sx={{ width: "100%" }}>
          {!isLite && (
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={value}
                variant="fullWidth"
                onChange={handleChange}
                textColor="inherit"
                aria-label="request tabs"
                indicatorColor="secondary"
              >
                <Tab label="Pending" iconPosition="start" {...a11yProps(0)} />

                <Tab
                  label="Closed"
                  iconPosition="start"
                  onClick={() => navigate("/requests/closed")}
                  {...a11yProps(1)}
                />
              </Tabs>
            </Box>
          )}

          <CustomTabPanel value={value} index={0}>
            <Paper elevation={3}>
              <TableContainer sx={{ mb: 4, maxHeight: 582 }}>
                <Table aria-label="collapsible table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {/* <TableCell /> */}

                      {conf.COLUMNS.REQUESTS.PENDING.map((column) => (
                        <TableCell
                          key={column.key}
                          sortDirection={order}
                          align={column.isHeader ? "left" : "right"}
                        >
                          <TableSortLabel
                            active={sort === column.key}
                            direction={sort === column.key ? order : "asc"}
                            onClick={(event: React.MouseEvent<unknown>) =>
                              handleSort(event, column.key)
                            }
                          >
                            <b>{column.label}</b>
                            {sort === column.key ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pending.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <LinearIndeterminate />
                        </TableCell>
                      </TableRow>
                    ) : pending.data?.data.length === 0 ? (
                      <EmptyTableRow colSpan={7} type="requests" />
                    ) : (
                      pending.data?.data?.map((request: RequestSchema) => (
                        <PendingRow
                          key={request.id}
                          reject={reject}
                          approve={approve}
                          request={request}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </CustomTabPanel>
        </Box>
      </Container>
    </ProtectedRoute>
  );
};

export default RequestsPending;
